// src/pages/admin/Settings.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { settingsApi } from "../../api/settingsApi";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Stack,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
  useMediaQuery,
  useTheme,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Save,
  Refresh,
  Settings as SettingsIcon,
  Security,
  Payment,
  Email,
  CloudUpload,
  AccountBalance,
  CheckCircle,
  Close,
  People,
  Edit,
} from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const [vendors, setVendors] = useState([]);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [commissionRate, setCommissionRate] = useState(0);

  const [settings, setSettings] = useState({
    siteName: "",
    siteLogo: "",
    siteDescription: "",
    maintenanceMode: false,
    currency: "ETB",
    language: "am",
    timezone: "Africa/Addis_Ababa",

    // Siinqee ONLY
    siinqeeEnabled: false,
    siinqeeMerchantId: "",
    siinqeeApiKey: "",
    siinqeeApiSecret: "",
    siinqeeEnvironment: "sandbox",
    cashOnDeliveryEnabled: false,
    minimumPayout: 500,

    requireVendorApproval: true,
    autoAcceptOrders: false,
    commissionRate: 10,

    smtpHost: "",
    smtpPort: 587,
    smtpSecure: true,
    smtpUser: "",
    smtpPass: "",

    enableTwoFactor: false,
    sessionTimeout: 60,
    debugMode: false,
    cacheEnabled: true,
  });

  useEffect(() => {
    fetchSettings();
    fetchVendors();
  }, []);

  // ✅ STRICT BOOLEAN PARSER - Handles all cases
  const strictBool = (val) => {
    if (val === true || val === 1 || val === '1' || val === 'true') return true;
    return false;
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getAdminSettings();

      let settingsData = {};
      if (response?.data?.settings) settingsData = response.data.settings;
      else if (response?.settings) settingsData = response.settings;
      else if (response?.data) settingsData = response.data;
      else if (response) settingsData = response;

      // ✅ FIX: Handle malformed logo URL
      let logo = settingsData.siteLogo || "";
      if (logo.includes("https://api.vtech-solution.comhttps")) {
        logo = logo.replace(
          "https://api.vtech-solution.comhttps//",
          "https://api.vtech-solution.com/"
        );
        logo = logo.replace(
          "https://api.vtech-solution.comhttps://",
          "https://api.vtech-solution.com/"
        );
      }

      setSettings((prev) => ({
        ...prev,
        siteName: settingsData.siteName || "V-Tech E-Commerce",
        siteLogo: logo,
        siteDescription: settingsData.siteDescription || "",
        currency: settingsData.currency || "ETB",
        language: settingsData.language || "am",
        timezone: settingsData.timezone || "Africa/Addis_Ababa",

        siinqeeEnabled: strictBool(settingsData.siinqeeEnabled),
        siinqeeMerchantId: settingsData.siinqeeMerchantId || "",
        siinqeeApiKey: settingsData.siinqeeApiKey || "",
        siinqeeApiSecret: settingsData.siinqeeApiSecret || "",
        siinqeeEnvironment: settingsData.siinqeeEnvironment || "sandbox",
        cashOnDeliveryEnabled: strictBool(settingsData.cashOnDeliveryEnabled),
        minimumPayout: settingsData.minimumPayout || 500,

        requireVendorApproval: strictBool(settingsData.requireVendorApproval),
        autoAcceptOrders: strictBool(settingsData.autoAcceptOrders),
        commissionRate: settingsData.commissionRate || 10,

        smtpHost: settingsData.smtpHost || "",
        smtpPort: settingsData.smtpPort || 587,
        smtpSecure: strictBool(settingsData.smtpSecure),
        smtpUser: settingsData.smtpUser || "",
        smtpPass: settingsData.smtpPass || "",

        enableTwoFactor: strictBool(settingsData.enableTwoFactor),
        sessionTimeout: settingsData.sessionTimeout || 60,
        debugMode: strictBool(settingsData.debugMode),
        cacheEnabled: strictBool(settingsData.cacheEnabled),
      }));

      setSuccess("Settings loaded successfully!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      console.error("Error fetching settings:", error);
      setError(error?.response?.data?.message || "Failed to load settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await settingsApi.getVendors();
      const vendorsData =
        response.vendors || response.data?.vendors || response.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        siteName: settings.siteName,
        siteLogo: settings.siteLogo,
        siteDescription: settings.siteDescription,
        maintenanceMode: settings.maintenanceMode ? "1" : "0",
        currency: settings.currency,
        language: settings.language,
        timezone: settings.timezone,

        siinqeeEnabled: settings.siinqeeEnabled ? "1" : "0",
        siinqeeMerchantId: settings.siinqeeMerchantId || "",
        siinqeeApiKey: settings.siinqeeApiKey || "",
        siinqeeApiSecret: settings.siinqeeApiSecret || "",
        siinqeeEnvironment: settings.siinqeeEnvironment || "sandbox",
        cashOnDeliveryEnabled: settings.cashOnDeliveryEnabled ? "1" : "0",
        minimumPayout: settings.minimumPayout || 500,

        requireVendorApproval: settings.requireVendorApproval ? "1" : "0",
        autoAcceptOrders: settings.autoAcceptOrders ? "1" : "0",
        commissionRate: settings.commissionRate || 10,

        smtpHost: settings.smtpHost || "",
        smtpPort: settings.smtpPort || 587,
        smtpSecure: settings.smtpSecure ? "1" : "0",
        smtpUser: settings.smtpUser || "",
        smtpPass: settings.smtpPass || "",

        enableTwoFactor: settings.enableTwoFactor ? "1" : "0",
        sessionTimeout: settings.sessionTimeout || 60,
        debugMode: settings.debugMode ? "1" : "0",
        cacheEnabled: settings.cacheEnabled ? "1" : "0",
      };

      const response = await settingsApi.updateAdminSettings(payload);

      if (
        response?.success ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        setSuccess("Settings saved successfully!");
        await fetchSettings();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response?.message || "Failed to save settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setError(error?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Updated logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, WEBP, or SVG)');
      e.target.value = null;
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      e.target.value = null;
      return;
    }

    setLogoUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append("logo", file);

    try {
      const response = await settingsApi.uploadLogo(formData);

      let logoUrl = "";
      if (response?.fullLogoUrl) logoUrl = response.fullLogoUrl;
      else if (response?.logoUrl) logoUrl = response.logoUrl;
      else if (response?.data?.fullLogoUrl) logoUrl = response.data.fullLogoUrl;
      else if (response?.data?.logoUrl) logoUrl = response.data.logoUrl;

      if (logoUrl) {
        // Fix malformed URLs if needed
        if (logoUrl.includes("https://api.vtech-solution.comhttps")) {
          logoUrl = logoUrl.replace(
            "https://api.vtech-solution.comhttps//",
            "https://api.vtech-solution.com/"
          );
          logoUrl = logoUrl.replace(
            "https://api.vtech-solution.comhttps://",
            "https://api.vtech-solution.com/"
          );
        }
        setSettings((prev) => ({ ...prev, siteLogo: logoUrl }));
        setSuccess("Logo uploaded successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to get logo URL from response");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      setError(error?.response?.data?.message || "Failed to upload logo. Please try again.");
    } finally {
      setLogoUploading(false);
      // Reset file input
      e.target.value = null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 12 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              System Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure platform settings, payment gateways, and vendor
              commissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: "100%", sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSettings}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => setTabValue(val)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
        >
          <Tab label="General" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Payments" icon={<Payment />} iconPosition="start" />
          <Tab
            label="Vendors & Commission"
            icon={<People />}
            iconPosition="start"
          />
          <Tab label="Email" icon={<Email />} iconPosition="start" />
          <Tab label="Security" icon={<Security />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <SettingsIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                General Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Site Name"
                  value={settings.siteName || ""}
                  onChange={(e) => handleChange("siteName", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Site Description"
                  value={settings.siteDescription || ""}
                  onChange={(e) =>
                    handleChange("siteDescription", e.target.value)
                  }
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={settings.currency || "ETB"}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Language"
                  value={settings.language || "am"}
                  onChange={(e) => handleChange("language", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Avatar
                    src={settings.siteLogo || ""}
                    variant="rounded"
                    sx={{ width: 64, height: 64 }}
                  >
                    {settings.siteName?.[0] || "V"}
                  </Avatar>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    disabled={logoUploading}
                    size={isMobile ? "small" : "medium"}
                  >
                    {logoUploading ? "Uploading..." : "Upload Logo"}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Button>
                  {logoUploading && <CircularProgress size={24} />}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceMode || false}
                      onChange={(e) =>
                        handleChange("maintenanceMode", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Maintenance Mode"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            border: `2px solid ${settings.siinqeeEnabled ? "#4caf50" : "#ff9800"}`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalance color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Siinqee Bank Payment Gateway
                </Typography>
                <Chip
                  label={
                    settings.siinqeeEnvironment === "production"
                      ? "Live"
                      : "Sandbox"
                  }
                  size="small"
                  color={
                    settings.siinqeeEnvironment === "production"
                      ? "success"
                      : "warning"
                  }
                  sx={{ ml: 1 }}
                />
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.siinqeeEnabled || false}
                      onChange={(e) =>
                        handleChange("siinqeeEnabled", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>Enable Siinqee Bank Payments</span>
                      {settings.siinqeeEnabled ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Active"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<Close />}
                          label="Disabled"
                          size="small"
                          color="error"
                        />
                      )}
                    </Stack>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Merchant ID"
                  value={settings.siinqeeMerchantId || ""}
                  onChange={(e) =>
                    handleChange("siinqeeMerchantId", e.target.value)
                  }
                  disabled={!settings.siinqeeEnabled}
                  size={isMobile ? "small" : "medium"}
                  helperText="Your Siinqee Bank merchant identifier"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="API Key"
                  value={settings.siinqeeApiKey || ""}
                  onChange={(e) =>
                    handleChange("siinqeeApiKey", e.target.value)
                  }
                  disabled={!settings.siinqeeEnabled}
                  size={isMobile ? "small" : "medium"}
                  type="password"
                  autoComplete="new-password"
                  helperText="Your Siinqee Bank API key"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="API Secret"
                  value={settings.siinqeeApiSecret || ""}
                  onChange={(e) =>
                    handleChange("siinqeeApiSecret", e.target.value)
                  }
                  disabled={!settings.siinqeeEnabled}
                  size={isMobile ? "small" : "medium"}
                  type="password"
                  autoComplete="new-password"
                  helperText="Your Siinqee Bank API secret"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={settings.siinqeeEnvironment || "sandbox"}
                    onChange={(e) =>
                      handleChange("siinqeeEnvironment", e.target.value)
                    }
                    disabled={!settings.siinqeeEnabled}
                    label="Environment"
                  >
                    <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                    <MenuItem value="production">Production (Live)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Siinqee Bank</strong> is the primary payment gateway
                    for Ethiopian transactions. All online payments will be
                    processed through this integration.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Payment color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Additional Payment Options
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.cashOnDeliveryEnabled)}
                      onChange={(e) =>
                        handleChange(
                          "cashOnDeliveryEnabled",
                          e.target.checked
                        )
                      }
                      color="primary"
                    />
                  }
                  label="Cash on Delivery"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Payout (ETB)"
                  value={settings.minimumPayout || 500}
                  onChange={(e) =>
                    handleChange(
                      "minimumPayout",
                      parseInt(e.target.value) || 0
                    )
                  }
                  size={isMobile ? "small" : "medium"}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="Minimum amount vendors can request for payout"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <People color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Vendor Commission Management
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {vendors.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No vendors registered yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell align="center">Commission Rate</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors.map((vendor) => (
                      <TableRow key={vendor.id}>
                        <TableCell>
                          {vendor.storeName || vendor.name || "Unnamed"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${vendor.commissionRate || 0}%`}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedVendor(vendor);
                              setCommissionRate(vendor.commissionRate || 0);
                              setCommissionDialogOpen(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Email color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Email Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={settings.smtpHost || ""}
                  onChange={(e) => handleChange("smtpHost", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="SMTP Port"
                  value={settings.smtpPort || 587}
                  onChange={(e) =>
                    handleChange("smtpPort", parseInt(e.target.value) || 587)
                  }
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.smtpSecure)}
                      onChange={(e) =>
                        handleChange("smtpSecure", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Use Secure Connection"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={settings.smtpUser || ""}
                  onChange={(e) => handleChange("smtpUser", e.target.value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMTP Password"
                  value={settings.smtpPass || ""}
                  onChange={(e) => handleChange("smtpPass", e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Security color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Security Settings
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.requireVendorApproval)}
                      onChange={(e) =>
                        handleChange(
                          "requireVendorApproval",
                          e.target.checked
                        )
                      }
                      color="primary"
                    />
                  }
                  label="Require Vendor Approval"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.autoAcceptOrders)}
                      onChange={(e) =>
                        handleChange("autoAcceptOrders", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Auto Accept Orders"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.enableTwoFactor)}
                      onChange={(e) =>
                        handleChange("enableTwoFactor", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Enable Two-Factor Authentication"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Session Timeout (minutes)"
                  value={settings.sessionTimeout || 60}
                  onChange={(e) =>
                    handleChange(
                      "sessionTimeout",
                      parseInt(e.target.value) || 60
                    )
                  }
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.debugMode)}
                      onChange={(e) =>
                        handleChange("debugMode", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Debug Mode"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.cacheEnabled)}
                      onChange={(e) =>
                        handleChange("cacheEnabled", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Enable Caching"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Commission</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Commission Rate (%)"
            value={commissionRate}
            onChange={(e) => setCommissionRate(parseFloat(e.target.value) || 0)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await settingsApi.updateVendorCommission({
                  vendorId: selectedVendor?.id,
                  commissionRate,
                });
                setCommissionDialogOpen(false);
                setSuccess("Commission updated successfully!");
                setTimeout(() => setSuccess(null), 3000);
                fetchVendors();
              } catch (error) {
                setError("Failed to update commission");
                console.error("Error updating commission:", error);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSettings;